import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Lottie from "react-lottie";
import foodPrepAnimation from "../../../../../lottie/foodprep.json";
import chefMakingPizzaAnimation from "../../../../../lottie/chefmakingpizza.json";
import restaurantFoodLoadingAnimation from "../../../../../lottie/RestaurantFoodLoading.json";
import ColumnBackgroundMedia from "../columnBackgroundMedia/ColumnBackgroundMedia";

const PreparingOrderCard2 = ({ displayMode }) => {
  const { fontColor, prepareBG, prepareFontBG, kotTokenData, fontSize,
    dineinColor, takeawayColor, deliveryColor, useOrderTypeColors,
    showPreparingTimer,
    prepareColumnBgEnabled, prepareColumnBgOpacity, prepareColumnBgMediaIds, columnBgIntervalMs
  } = useSelector(
    (state) => state.kotTokenDisplaySlice
  );

  // Get background color based on order type (only if useOrderTypeColors is enabled)
  const getOrderTypeColor = (ordType) => {
    if (!useOrderTypeColors) {
      return prepareFontBG || "#994529";
    }
    switch (ordType) {
      case "TAKEAWAY":
        return takeawayColor || "#1976d2";
      case "DELIVERY":
        return deliveryColor || "#388e3c";
      case "DINEIN":
      default:
        return dineinColor || prepareFontBG || "#994529";
    }
  };

  // Support both classic structure (object with preparing array)
  // and any future array-wrapped structure for safety
  const rawData = Array.isArray(kotTokenData)
    ? kotTokenData[0]?.preparing
    : kotTokenData?.preparing;
  const data = Array.isArray(rawData) ? rawData : [];

  // Single grid like classic - all order types mixed
  const columns = fontSize === "large" || fontSize === "xlarge" ? 2 : 3;
  const chunkSize = fontSize === "xlarge" ? 16 : fontSize === "medium" ? 36 : 20;
  const totalChunks = Math.ceil(data.length / chunkSize);
  const [currentChunk, setCurrentChunk] = useState(0);

  // Get the current data slice
  const chunkData = data.slice(
    currentChunk * chunkSize,
    currentChunk * chunkSize + chunkSize
  );

  const [timers, setTimers] = useState({});
  const [animatedTokens, setAnimatedTokens] = useState({}); // first food-prep animation played
  const [currentAnimatingToken, setCurrentAnimatingToken] = useState(null);

  // Second-stage chef animation (after delay, per token, once)
  const [chefAnimatedTokens, setChefAnimatedTokens] = useState({});
  const [currentChefAnimatingToken, setCurrentChefAnimatingToken] = useState(null);

  // Third-stage loading animation (after more delay, per token, once)
  const [loadingAnimatedTokens, setLoadingAnimatedTokens] = useState({});
  const [currentLoadingAnimatingToken, setCurrentLoadingAnimatingToken] = useState(null);

  const containerRef = useRef();

  // Timer formatting
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Auto-scroll chunks
  useEffect(() => {
    if (data.length <= chunkSize) return;
    const interval = setInterval(() => {
      setCurrentChunk((prev) => (prev + 1) % totalChunks);
    }, 7000);
    return () => clearInterval(interval);
  }, [data.length, chunkSize, totalChunks]);

  // Load timers from localStorage on mount
  useEffect(() => {
    const savedTimers = localStorage.getItem("preparingTimers");
    if (savedTimers) {
      setTimers(JSON.parse(savedTimers));
    }
  }, []);

  // Timer increment with localStorage sync
  useEffect(() => {
    const interval = setInterval(() => {
      setTimers((prev) => {
        const updated = { ...prev };
        chunkData.forEach((res) => {
          const key = res.transNo;
          if (key != null) {
            updated[key] = (updated[key] || 0) + 1;
          }
        });
        localStorage.setItem("preparingTimers", JSON.stringify(updated));
        return updated;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [chunkData]);

  // Load which tokens have already played animation (per browser)
  useEffect(() => {
    const saved = localStorage.getItem("preparingAnimationPlayed");
    if (saved) {
      try {
        setAnimatedTokens(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to parse preparingAnimationPlayed from localStorage", e);
      }
    }
    const savedChef = localStorage.getItem("preparingChefAnimationPlayed");
    if (savedChef) {
      try {
        setChefAnimatedTokens(JSON.parse(savedChef));
      } catch (e) {
        console.error("Failed to parse preparingChefAnimationPlayed from localStorage", e);
      }
    }
    const savedLoading = localStorage.getItem("preparingLoadingAnimationPlayed");
    if (savedLoading) {
      try {
        setLoadingAnimatedTokens(JSON.parse(savedLoading));
      } catch (e) {
        console.error("Failed to parse preparingLoadingAnimationPlayed from localStorage", e);
      }
    }
  }, []);

  // Pick the first visible token that has not yet played animation
  useEffect(() => {
    const visibleTokens = chunkData
      .map((res) => res?.transNo)
      .filter((x) => x !== null && x !== undefined);

    if (
      currentAnimatingToken !== null &&
      visibleTokens.includes(currentAnimatingToken) &&
      !animatedTokens[currentAnimatingToken]
    ) {
      return;
    }

    const next = visibleTokens.find((t) => !animatedTokens[t]);
    if (next !== undefined) {
      setCurrentAnimatingToken(next);
    } else {
      setCurrentAnimatingToken(null);
    }
  }, [chunkData, animatedTokens, currentAnimatingToken]);

  const handleAnimationComplete = (tokenNo) => {
    if (tokenNo == null) return;
    setAnimatedTokens((prev) => {
      const updated = { ...prev, [tokenNo]: true };
      localStorage.setItem("preparingAnimationPlayed", JSON.stringify(updated));
      return updated;
    });
  };

  // After text has been showing for a while, trigger chef animation once per token
  useEffect(() => {
    const visibleTokens = chunkData
      .map((res) => res?.transNo)
      .filter((x) => x !== null && x !== undefined);

    if (
      currentChefAnimatingToken !== null &&
      visibleTokens.includes(currentChefAnimatingToken) &&
      !chefAnimatedTokens[currentChefAnimatingToken]
    ) {
      return;
    }

    const CHEF_DELAY_SECONDS = 20;
    const nextChef = visibleTokens.find((t) => {
      return (
        animatedTokens[t] &&
        !chefAnimatedTokens[t] &&
        (timers[t] || 0) >= CHEF_DELAY_SECONDS
      );
    });

    if (nextChef !== undefined) {
      setCurrentChefAnimatingToken(nextChef);
    } else {
      setCurrentChefAnimatingToken(null);
    }
  }, [chunkData, animatedTokens, chefAnimatedTokens, timers, currentChefAnimatingToken]);

  const handleChefAnimationComplete = (tokenNo) => {
    if (tokenNo == null) return;
    setChefAnimatedTokens((prev) => {
      const updated = { ...prev, [tokenNo]: true };
      localStorage.setItem("preparingChefAnimationPlayed", JSON.stringify(updated));
      return updated;
    });
  };

  // Third-stage loading animation selection (after chef, extra delay)
  useEffect(() => {
    const visibleTokens = chunkData
      .map((res) => res?.transNo)
      .filter((x) => x !== null && x !== undefined);

    if (
      currentLoadingAnimatingToken !== null &&
      visibleTokens.includes(currentLoadingAnimatingToken) &&
      !loadingAnimatedTokens[currentLoadingAnimatingToken]
    ) {
      return;
    }

    const LOADING_EXTRA_DELAY_SECONDS = 40;
    const nextLoading = visibleTokens.find((t) => {
      return (
        animatedTokens[t] &&
        chefAnimatedTokens[t] &&
        !loadingAnimatedTokens[t] &&
        (timers[t] || 0) >= LOADING_EXTRA_DELAY_SECONDS
      );
    });

    if (nextLoading !== undefined) {
      setCurrentLoadingAnimatingToken(nextLoading);
    } else {
      setCurrentLoadingAnimatingToken(null);
    }
  }, [chunkData, animatedTokens, chefAnimatedTokens, loadingAnimatedTokens, timers, currentLoadingAnimatingToken]);

  const handleLoadingAnimationComplete = (tokenNo) => {
    if (tokenNo == null) return;
    setLoadingAnimatedTokens((prev) => {
      const updated = { ...prev, [tokenNo]: true };
      localStorage.setItem("preparingLoadingAnimationPlayed", JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <div className="preparing-card-container">
      <div className="order-status-type-container">
        <h3 className="token-display-status-header" style={{ padding: "10px 0" }}>PREPARING</h3>
      </div>
      <div style={{ backgroundColor: prepareBG, height: "-webkit-fill-available", position: "relative", overflow: "hidden" }}>
        {prepareColumnBgEnabled && Array.isArray(prepareColumnBgMediaIds) && prepareColumnBgMediaIds.length > 0 && (
          <ColumnBackgroundMedia mediaIds={prepareColumnBgMediaIds} opacityPercent={prepareColumnBgOpacity} intervalMs={columnBgIntervalMs} />
        )}
        <div
          ref={containerRef}
          className="token-status-card-body"
          style={{
            fontSize: fontSize === "xlarge" ? "46px" : fontSize === "large" ? "38px" : "30px",
            display: "grid",
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap: fontSize === "xlarge" ? "14px" : "12px",
            padding: fontSize === "xlarge" ? "14px" : "10px",
            position: "relative",
            zIndex: 1,
          }}
        >
          {chunkData?.map((res, i) => (
            <div
              key={res?.transNo || i}
              style={{
                backgroundColor: getOrderTypeColor(res?.ordType),
                padding: "3px",
                display: "flex",
                borderRadius: "3px",
              }}
            >
              {/* Left: Token number + timer */}
              <div
                className="token-number-box"
                style={{
                  alignContent: "center",
                  textAlign: "center",
                  justifyItems: "center",
                  backgroundColor: "unset",
                  color: fontColor,
                  borderRadius: "0px",
                  width: "42%",
                  padding: "8px 0",
                }}
              >
                <div style={{ fontSize: fontSize === "xlarge" ? "34px" : fontSize === "large" ? "30px" : "25px" }}>{res?.transNo}</div>
                {showPreparingTimer && (
                  <div
                    style={{
                      fontSize: fontSize === "xlarge" ? "16px" : "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: fontColor,
                      fontWeight: "bold",
                    }}
                  >
                    <span>{formatTime(timers[res.transNo] || 0)}</span>
                  </div>
                )}
                {(res?.ordType === "TAKEAWAY" || res?.ordType === "DELIVERY") && (
                  <div
                    style={{
                      fontSize: "10px",
                      fontWeight: "bold",
                      color: fontColor,
                      marginTop: "2px",
                    }}
                  >
                    {res?.ordType === "TAKEAWAY" ? "TAKE AWAY" : "DELIVERY"}
                  </div>
                )}
              </div>

              {/* Right: Animation / Text */}
              <div
                style={{
                  width: "58%",
                  textAlign: "center",
                  backgroundColor: fontColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "4px 6px",
                  boxSizing: "border-box",
                  overflow: "hidden",
                }}
              >
                {currentAnimatingToken === res.transNo ? (
                  <Lottie
                    options={{
                      loop: false,
                      autoplay: true,
                      animationData: foodPrepAnimation,
                      rendererSettings: {
                        preserveAspectRatio: "xMidYMid slice",
                      },
                    }}
                    height={fontSize === "xlarge" ? 80 : 60}
                    width={fontSize === "xlarge" ? 80 : 60}
                    eventListeners={[
                      {
                        eventName: "complete",
                        callback: () => handleAnimationComplete(res.transNo),
                      },
                    ]}
                  />
                ) : currentChefAnimatingToken === res.transNo ? (
                  <Lottie
                    options={{
                      loop: false,
                      autoplay: true,
                      animationData: chefMakingPizzaAnimation,
                      rendererSettings: {
                        preserveAspectRatio: "xMidYMid slice",
                      },
                    }}
                    height={fontSize === "xlarge" ? 80 : 60}
                    width={fontSize === "xlarge" ? 80 : 60}
                    eventListeners={[
                      {
                        eventName: "complete",
                        callback: () => handleChefAnimationComplete(res.transNo),
                      },
                    ]}
                  />
                ) : currentLoadingAnimatingToken === res.transNo ? (
                  <Lottie
                    options={{
                      loop: false,
                      autoplay: true,
                      animationData: restaurantFoodLoadingAnimation,
                      rendererSettings: {
                        preserveAspectRatio: "xMidYMid slice",
                      },
                    }}
                    height={fontSize === "xlarge" ? 80 : 60}
                    width={fontSize === "xlarge" ? 80 : 60}
                    eventListeners={[
                      {
                        eventName: "complete",
                        callback: () => handleLoadingAnimationComplete(res.transNo),
                      },
                    ]}
                  />
                ) : (
                  <p
                    style={{
                      fontSize: fontSize === "xlarge" ? "18px" : fontSize === "large" ? "15px" : "12px",
                      fontWeight: "bold",
                      margin: 0,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100%",
                    }}
                  >
                    Preparing...
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PreparingOrderCard2;
